<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAddressCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    displayDefaultFlag: {
      type: Boolean,
      required: false,
      default: false,
    },
    compactDisplay: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.displayDefaultFlag,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayDefaultFlag');
    }
  );
  watch(
    () => props.compactDisplay,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('compactDisplay');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <address>
    <ItpBox v-if="!compactDisplay">
      <UcAddressTypeBadge :value="item.address_type"> </UcAddressTypeBadge>
      <ItpBadge
        :text="gettext('Default')"
        variant="info"
        no-box-shadow
        class="ms-3"
        v-if="item.is_default && displayDefaultFlag"
      >
      </ItpBadge>
    </ItpBox>
    <div v-if="compactDisplay && item.address">
      {{ item.address }}
    </div>
    <div v-if="!compactDisplay || !item.address">
      <template v-for="(line, index) in item.lines">
        <div :key="index" v-if="item.lines && item.lines.length">
          {{ line }}
        </div>
      </template>
      <div v-if="item.line_1">{{ item.line_1 }}<br /></div>
      <div v-if="item.line_2">{{ item.line_2 }}<br /></div>
      <div v-if="item.line_3">{{ item.line_3 }}<br /></div>
      <div v-if="item.street_address">{{ item.street_address }}<br /></div>
    </div>
    <div>{{ item.city }} {{ item.region }} {{ item.postal_code }}</div>
    <div v-if="item.country">
      {{ item.country }}
    </div>
    <ItpText :text="item.other_details" pre v-if="item.other_details">
    </ItpText>
  </address>
</template>
